import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { Store } from '@ngrx/store';
import { HighchartsService } from '../../services/highcharts/highcharts.service';
import {
  ElementRef,
  Input,
  Component,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { CurrencyPipe, DatePipe } from '@angular/common';

@AutoUnsubscribe()
@Component({
  selector: 'home-line-chart',
  styleUrls: ['home-line-chart.component.scss'],
  template: `<div class="chart-container">
    <div class="chart-wraper"></div>
  </div>`,
  providers: [DatePipe],
})
export class HomeLineChartComponent implements AfterViewInit, OnDestroy {
  @Input()
  set options(value) {
    this.optionsPrivate = value;
  }
  get options() {
    return this.optionsPrivate;
  }
  private seriesPrivate: any;
  private optionsPrivate: any;
  private chartInstancePrivate: any;
  private valuesArray: any[];
  private categories: any[];
  private dataGraphic: number[] = [0, 0, 0, 0];
  private balance: number = 0;
  constructor(
    public store: Store<AppState>,
    private elementReference: ElementRef,
    private highchartsService: HighchartsService,
    // private utilitiesService: UtilitiesService,
    private currencyPipe: CurrencyPipe,
    public datepipe: DatePipe
  ) {}

  public ngAfterViewInit() {
    // requisita ao serviço que plote o gráfico no container do component
    const categories = this.createCategoriesList();
    this.seriesPrivate = this.computeChartSeries();

    const tooltipFormatter = this.tooltipFormatter(
      this.optionsPrivate,
      this.formatCurrencyValue(),
      this.createMainLabel(),
      this.balance
    );

    const sizeChart = '220px';

    this.chartInstancePrivate = this.highchartsService.plotLineChart(
      this.elementReference.nativeElement,
      this.seriesPrivate,
      categories,
      this.options,
      tooltipFormatter,
      sizeChart,
      false,
      false,
      'center'
    );
  }

  /**
   * Atualiza a plotagem das séries em tempo real
   * (a medida que o valueInvest é alterado)
   */
  public updateChartSeries() {
    if (!this.chartInstancePrivate) return;

    this.seriesPrivate = this.computeChartSeries();
    for (let i = 0; i < this.seriesPrivate.length; i++) {
      for (let j = 0; j < this.seriesPrivate[i].data.length; j++) {
        if (
          this.seriesPrivate[i].data[j] !==
          this.chartInstancePrivate.instance.series[i].data[j].y
        ) {
          this.chartInstancePrivate.instance.series[i].data[j].update(
            this.seriesPrivate[i].data[j]
          );
        }
      }
    }

    // força a atualização da tooltip
    const chartInstance = this.chartInstancePrivate.instance;
    const pointsToRefresh = [];
    for (const key of chartInstance.series) {
      pointsToRefresh.push(key.points[key.points.length - 1]);
    }

    chartInstance.tooltip.refresh(pointsToRefresh);

    // desenha a crosshair no fim do gráfico (acompanhando a tooltip)
    const xAxis = chartInstance.xAxis[0];
    const points = chartInstance.series[0].points;

    xAxis.drawCrosshair(new Event(null), points[points.length - 1]);
  }

  /**
   * Calcula o valor das séries de acordo com o valor do investimento
   * @param valueInvest Valor estimado para o investimento
   */
  public computeChartSeries(): any[] {
    // calcula/recalcula as séries
    const series = new Array<any>();

    series.push({
      // rendimento CDB
      color: '#CC0000',
      name: 'CDB',
      data: this.dataGraphic,
      pointInterval: 1,
      marker: {
        enabled: false,
        symbol: 'url(assets/images/blue-ball.svg)',
        width: 16,
        height: 16,
        states: {
          hover: {
            enabled: true,
            radius: 1,
            lineWidth: 0,
          },
        },
      },
    });

    return series;
  }

  /**
   * Retorna os Dias , e os valores do cálculo dos próximos dias
   */
  public createCategoriesList(): string[] {
    const data = this.optionsPrivate.data;

    const dateD1 = this.datepipe.transform(new Date(data.dateD1), 'dd/MM');
    const dateD2 = this.datepipe.transform(new Date(data.dateD2), 'dd/MM');
    const dateD3 = this.datepipe.transform(new Date(data.dateD3), 'dd/MM');

    this.balance = data.balanceD0;

    const vprojectedD0 = 0;
    const projectedD0 = { value: 0, calc: vprojectedD0 };
    const vprojectedD1 = vprojectedD0 + +data.projectedD1;
    const projectedD1 = {
      value: data.projectedD1,
      calc: vprojectedD1 ? vprojectedD1 : vprojectedD0,
    };
    const vprojectedD2 = +projectedD1.calc + +data.projectedD2;
    const projectedD2 = {
      value: data.projectedD2,
      calc: vprojectedD2 ? vprojectedD2 : vprojectedD1,
    };
    const vprojectedD3 = +projectedD2.calc + +data.projectedD3;
    const projectedD3 = {
      value: data.projectedD3,
      calc: vprojectedD3 ? vprojectedD3 : vprojectedD2,
    };
    this.valuesArray = [projectedD0, projectedD1, projectedD2, projectedD3];
    this.dataGraphic = [
      projectedD0.calc,
      projectedD1.calc,
      projectedD2.calc,
      projectedD3.calc,
    ];
    this.categories = ['Hoje', dateD1, dateD2, dateD3];
    return this.categories;
  }

  /**
   *
   * @param options Definições do gráfico
   * @param formatValuesProvider Provider para formatar valores
   * @param createMainLabelProvider Provider para criar o label principal da tooltip
   */
  public tooltipFormatter(
    options,
    formatValuesProvider,
    createMainLabelProvider,
    balance
  ) {
    return function () {
      const valuesProvider = createMainLabelProvider(this.x);
      let classValue = 'value-normal';
      if (valuesProvider.value < 0) {
        classValue = 'value-negative';
      } else if (valuesProvider.value > 0) {
        classValue = 'value-positive';
      }
      const template = `
                <div class='tooltip-graphic-chart'>
                    <div class='tooltip-primary-chart'>
                      <span class='text-value-1 ${classValue}'>
                            ${formatValuesProvider(valuesProvider.value)}
                        </span>
                        <br>
                        <span class='text-value-2'>
                            ${formatValuesProvider(
                              valuesProvider.calc + balance
                            )}
                        </span>
                    </div>
                </div>`;

      return template;
    };
  }

  /**
   * Cria o texto da tooltip para o valor principal (valor do CDB) de acordo com as séries exibidas
   * e com o ano em questão.
   * @param time tempo (label do eixo)
   */
  public createMainLabel() {
    return (time: string, savings: boolean, banksAverage: boolean) => {
      time = time.toString();
      const pont = this.categories.indexOf(time);
      time = time.toString();
      return this.valuesArray[pont];
    };
  }

  /**
   * Formata valores para exibição em formato monetário
   * @param value valor a ser representado
   * @return Função para realizar a operação de acordo com o parâmetro fornecido
   */
  public formatCurrencyValue() {
    return (value) => this.currencyPipe.transform(value, 'BRL', true);
  }

  // não remover, necessário para o funcionamento correto do @AutoUnsubscribe
  // tslint:disable-next-line: no-empty
  public ngOnDestroy() {}
}
